import { useEffect, useMemo } from 'react';
import * as Y from 'yjs';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useHistoryPreviewStore } from '../../../stores/historyPreviewStore';
import { base64ToUint8Array } from '../../../utils/yjs';
import type { Scene } from '../../../core/models/scene';
import type { ViewDefinition } from '../../../core/repository/DiagramRepository';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import { buildOrgProjectionExportScene } from '../services/orgProjectionExportMaterializer';
import { useOrgModel } from './useOrgModel';

/** Render-only composite. Protected graph values never enter the history store or source Y.Doc. */
export function buildOrgHistoryPreviewScene(
  model: OrgModelProjection,
  view: ViewDefinition,
  snapshotBytes: string,
): Scene {
  if (model.completeness?.entities.hasMore || model.completeness?.relationships.hasMore) {
    throw new Error('Historical projection is truncated');
  }
  const presentation = new Y.Doc();
  try {
    Y.applyUpdate(presentation, base64ToUint8Array(snapshotBytes));
    return buildOrgProjectionExportScene(model, view, presentation);
  } finally {
    presentation.destroy();
  }
}

export function useOrgHistoryPreview(diagramId: string | undefined, enabled: boolean) {
  const scope = useHistoryPreviewStore((state) => state.scope);
  const active = Boolean(enabled && scope && scope.diagramId === diagramId);
  const { data: diagram, isError: infoError } = useDiagramInfo(enabled ? diagramId : undefined);
  const view = diagram?.viewDefinition;
  const sourceBacked = Boolean(view?.projection);
  const supported = view?.projection === 'people_chart' || view?.projection === 'position_chart';
  const validTimestamp = Boolean(scope?.createdAt && Number.isFinite(Date.parse(scope.createdAt)));
  const model = useOrgModel({
    folderId: view?.sourceFolderId ?? diagram?.folderId,
    diagramId,
    asOf: active ? scope?.createdAt : undefined,
    txAt: active ? scope?.createdAt : undefined,
    enabled: Boolean(active && supported && validTimestamp),
  });
  const result = useMemo(() => {
    if (!active || !scope || !diagram || !sourceBacked) return { scene: null, failed: false };
    if (!supported || !validTimestamp || model.isError) return { scene: null, failed: true };
    if (!model.data || !view) return { scene: null, failed: false };
    // Rolling deployments must not paint a current model from an older
    // server (which ignores txAt) as historical truth.
    if (model.data.txAt !== new Date(scope.createdAt).toISOString()
      || model.data.asOf !== new Date(scope.createdAt).toISOString()) return { scene: null, failed: true };
    try {
      const projectionView: ViewDefinition = {
        ...view, type: view.projection === 'position_chart' ? 'position-chart' : 'people-chart',
      };
      return { scene: buildOrgHistoryPreviewScene(model.data, projectionView, scope.snapshotBytes), failed: false };
    } catch {
      return { scene: null, failed: true };
    }
  }, [active, diagram, sourceBacked, supported, validTimestamp, model.data, model.isError, view, scope]);
  const managed = Boolean(active && (!diagram || sourceBacked));
  const status = !managed ? 'idle' : result.failed || infoError ? 'unavailable' : result.scene ? 'ready' : 'loading';
  useEffect(() => {
    if (active && scope) useHistoryPreviewStore.getState().setProjectionStatus(scope.requestId, status);
  }, [active, scope, status]);
  return { managed, scene: result.scene };
}
